import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { useSubscription, useRefetchSubscription } from '@/queries/subscriptions';
import { useAuth } from '@/contexts/AuthContext';
import { Confetti } from '@/components/Confetti';
import { CircularLoader } from '@/components/CircularLoader';
import { TIER_NAMES } from '@/types/subscription';
import styles from './success.module.scss';

export const Route = createFileRoute('/checkout/success')({
  component: CheckoutSuccess,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      session_id: search.session_id as string,
    };
  },
  beforeLoad: async ({ context, search }) => {
    // CRITICAL: Verify user is authenticated before showing success page
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      console.warn('Unauthorized access attempt to checkout success page');
      throw redirect({
        to: '/login',
        search: {
          redirect: '/pricing',
        },
      });
    }

    // Verify session_id is present
    if (!search.session_id) {
      console.warn('Missing session_id in checkout success page');
      throw redirect({
        to: '/pricing',
      });
    }
  },
});

function CheckoutSuccess() {
  useAuth();
  const navigate = useNavigate();
  Route.useSearch();
  const refetchSubscription = useRefetchSubscription();
  const [hasRefetched, setHasRefetched] = useState(false);

  // Poll for subscription status (webhook might take a few seconds)
  const { data: subscription, isLoading } = useSubscription();

  useEffect(() => {
    // Refetch subscription data when page loads
    if (!hasRefetched) {
      refetchSubscription();
      setHasRefetched(true);
    }
  }, [hasRefetched, refetchSubscription]);

  useEffect(() => {
    // Redirect to home after 5 seconds if subscription is active
    if (subscription?.status === 'active') {
      const timer = setTimeout(() => {
        navigate({ to: '/' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [subscription, navigate]);

  if (isLoading || !subscription) {
    return (
      <div className={styles.success}>
        <div className={styles.success__loading}>
          <CircularLoader />
          <h2>Processing your subscription...</h2>
          <p>This should only take a moment.</p>
        </div>
      </div>
    );
  }

  const tierName = subscription.tier ? TIER_NAMES[subscription.tier] : 'Unknown';

  return (
    <div className={styles.success}>
      {/* Celebration animation */}
      <Confetti />

      <motion.div
        className={styles.success__content}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', duration: 0.5 }}
      >
        {/* Animated checkmark */}
        <motion.div
          className={styles.success__iconContainer}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.2 }}
        >
          <motion.svg
            className={styles.success__icon}
            viewBox="0 0 52 52"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <circle
              className={styles.success__iconCircle}
              cx="26"
              cy="26"
              r="25"
              fill="none"
            />
            <motion.path
              className={styles.success__iconCheck}
              fill="none"
              d="M14.1 27.2l7.1 7.2 16.7-16.8"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            />
          </motion.svg>
        </motion.div>

        <motion.h1
          className={styles.success__title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          Welcome to {tierName}!
        </motion.h1>

        <motion.p
          className={styles.success__subtitle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          Your subscription has been activated successfully.
        </motion.p>

        <motion.div
          className={styles.success__details}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <div className={styles.success__detail}>
            <strong>Plan:</strong> {tierName}
          </div>
          <div className={styles.success__detail}>
            <strong>Status:</strong> {subscription.status}
          </div>
          {subscription.current_period_end && (
            <div className={styles.success__detail}>
              <strong>Next billing:</strong>{' '}
              {new Date(subscription.current_period_end).toLocaleDateString()}
            </div>
          )}
        </motion.div>

        <motion.button
          className={styles.success__button}
          onClick={() => navigate({ to: '/' })}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Start Creating Quizzes
        </motion.button>

        <motion.p
          className={styles.success__redirectNote}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
        >
          Redirecting in 5 seconds...
        </motion.p>
      </motion.div>
    </div>
  );
}
